import { c as _c } from "react/compiler-runtime";
import { Input, InputGroup, type InputVariants } from '@systemeio/components';
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { useFormControlSize } from './form-control-size-context';
import { InputWithSelects } from './input-with-selects';
import { SafeHtmlText } from './safe-html-text';
interface InputProps extends Omit<ComponentPropsWithoutRef<'input'>, 'className' | 'prefix' | 'size'> {
  className?: string;
  description?: ReactNode;
  error?: string | null;
  isPreFetching?: boolean;
  label?: ReactNode;
  size?: InputVariants['size'];
}
interface FormInputGroupProps extends InputProps {
  inputClassName?: string;
  leadingIcon?: ReactNode;
  prefix?: ReactNode;
  prefixClassName?: string;
  statusIcon?: ReactNode;
  startControl?: ReactNode;
  suffix?: ReactNode;
  trailingAction?: ReactNode;
}
const hasContent = (content: ReactNode) => content !== undefined && content !== null && content !== false;
interface InputFieldProps extends FormInputGroupProps {
  grouped?: boolean;
}
const InputField = forwardRef<HTMLInputElement, InputFieldProps>(function InputField(t0, ref) {
  const $ = _c(71);
  let ariaDescribedBy;
  let className;
  let description;
  let disabled;
  let error;
  let grouped;
  let id;
  let inputClassName;
  let inputProps;
  let isPreFetching;
  let label;
  let leadingIcon;
  let prefix;
  let prefixClassName;
  let required;
  let size;
  let startControl;
  let statusIcon;
  let suffix;
  let trailingAction;
  if ($[0] !== t0) {
    ({
      "aria-describedby": ariaDescribedBy,
      className,
      description,
      disabled,
      error,
      id,
      isPreFetching,
      inputClassName,
      label,
      leadingIcon,
      prefix,
      prefixClassName,
      required,
      size,
      startControl,
      statusIcon,
      suffix,
      trailingAction,
      grouped,
      ...inputProps
    } = t0);
    $[0] = t0;
    $[1] = ariaDescribedBy;
    $[2] = className;
    $[3] = description;
    $[4] = disabled;
    $[5] = error;
    $[6] = grouped;
    $[7] = id;
    $[8] = inputClassName;
    $[9] = inputProps;
    $[10] = isPreFetching;
    $[11] = label;
    $[12] = leadingIcon;
    $[13] = prefix;
    $[14] = prefixClassName;
    $[15] = required;
    $[16] = size;
    $[17] = startControl;
    $[18] = statusIcon;
    $[19] = suffix;
    $[20] = trailingAction;
  } else {
    ariaDescribedBy = $[1];
    className = $[2];
    description = $[3];
    disabled = $[4];
    error = $[5];
    grouped = $[6];
    id = $[7];
    inputClassName = $[8];
    inputProps = $[9];
    isPreFetching = $[10];
    label = $[11];
    leadingIcon = $[12];
    prefix = $[13];
    prefixClassName = $[14];
    required = $[15];
    size = $[16];
    startControl = $[17];
    statusIcon = $[18];
    suffix = $[19];
    trailingAction = $[20];
  }
  const inheritedSize = useFormControlSize();
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const descriptionId = description ? `${inputId}-description` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  let t1;
  if ($[21] !== ariaDescribedBy || $[22] !== descriptionId || $[23] !== errorId) {
    t1 = [ariaDescribedBy, descriptionId, errorId].filter(Boolean).join(" ") || undefined;
    $[21] = ariaDescribedBy;
    $[22] = descriptionId;
    $[23] = errorId;
    $[24] = t1;
  } else {
    t1 = $[24];
  }
  const describedBy = t1;
  const isDisabled = disabled || isPreFetching;
  const resolvedSize = size ?? inheritedSize;
  let t2;
  if ($[25] !== inputProps) {
    t2 = "value" in inputProps ? {
      value: inputProps.value ?? ""
    } : {};
    $[25] = inputProps;
    $[26] = t2;
  } else {
    t2 = $[26];
  }
  const t3 = error ? true : undefined;
  const t4 = error ? "" : undefined;
  let t5;
  if ($[27] !== describedBy || $[28] !== inputId || $[29] !== inputProps || $[30] !== isDisabled || $[31] !== required || $[32] !== t2 || $[33] !== t3 || $[34] !== t4) {
    t5 = {
      ...inputProps,
      ...t2,
      "aria-describedby": describedBy,
      "aria-invalid": t3,
      "data-invalid": t4,
      disabled: isDisabled,
      id: inputId,
      required
    };
    $[27] = describedBy;
    $[28] = inputId;
    $[29] = inputProps;
    $[30] = isDisabled;
    $[31] = required;
    $[32] = t2;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  const controlProps = t5;
  let t6;
  if ($[36] !== controlProps || $[37] !== grouped || $[38] !== inputClassName || $[39] !== leadingIcon || $[40] !== prefix || $[41] !== prefixClassName || $[42] !== ref || $[43] !== resolvedSize || $[44] !== statusIcon || $[45] !== suffix || $[46] !== trailingAction) {
    t6 = grouped ? <InputGroup.Root size={resolvedSize}><InputGroup.Input {...controlProps} ref={ref} className={inputClassName} />{hasContent(leadingIcon) && <InputGroup.Addon align="inline-start">{leadingIcon}</InputGroup.Addon>}{hasContent(prefix) && <InputGroup.Addon align="inline-start" appearance="filled" className={prefixClassName}>{prefix}</InputGroup.Addon>}{hasContent(suffix) && <InputGroup.Addon align="inline-end" appearance="filled">{suffix}</InputGroup.Addon>}{hasContent(statusIcon) && <InputGroup.Addon align="inline-end">{statusIcon}</InputGroup.Addon>}{hasContent(trailingAction) && <InputGroup.Addon align="inline-end">{trailingAction}</InputGroup.Addon>}</InputGroup.Root> : <Input {...controlProps} ref={ref} size={resolvedSize} />;
    $[36] = controlProps;
    $[37] = grouped;
    $[38] = inputClassName;
    $[39] = leadingIcon;
    $[40] = prefix;
    $[41] = prefixClassName;
    $[42] = ref;
    $[43] = resolvedSize;
    $[44] = statusIcon;
    $[45] = suffix;
    $[46] = trailingAction;
    $[47] = t6;
  } else {
    t6 = $[47];
  }
  const control = t6;
  const t7 = isPreFetching || undefined;
  const t8 = isPreFetching && "animate-pulse";
  let t9;
  if ($[48] !== className || $[49] !== t8) {
    t9 = twMerge("grid w-full content-start gap-2", t8, className);
    $[48] = className;
    $[49] = t8;
    $[50] = t9;
  } else {
    t9 = $[50];
  }
  let t10;
  if ($[51] !== inputId || $[52] !== label || $[53] !== required) {
    t10 = hasContent(label) && <label className="text-text-secondary flex" htmlFor={inputId}>{label}{required && <span className="text-text-error"> *</span>}</label>;
    $[51] = inputId;
    $[52] = label;
    $[53] = required;
    $[54] = t10;
  } else {
    t10 = $[54];
  }
  let t11;
  if ($[55] !== control || $[56] !== startControl) {
    t11 = hasContent(startControl) ? <InputWithSelects input={control} startSelect={startControl} /> : control;
    $[55] = control;
    $[56] = startControl;
    $[57] = t11;
  } else {
    t11 = $[57];
  }
  let t12;
  if ($[58] !== description || $[59] !== descriptionId) {
    t12 = description && <span className="text-text-secondary text-sm" id={descriptionId}>{description}</span>;
    $[58] = description;
    $[59] = descriptionId;
    $[60] = t12;
  } else {
    t12 = $[60];
  }
  let t13;
  if ($[61] !== error || $[62] !== errorId) {
    t13 = error && <SafeHtmlText as="p" className="text-text-error text-sm" html={error} id={errorId} />;
    $[61] = error;
    $[62] = errorId;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] !== t10 || $[65] !== t11 || $[66] !== t12 || $[67] !== t13 || $[68] !== t7 || $[69] !== t9) {
    t14 = <div aria-busy={t7} className={t9}>{t10}{t11}{t12}{t13}</div>;
    $[64] = t10;
    $[65] = t11;
    $[66] = t12;
    $[67] = t13;
    $[68] = t7;
    $[69] = t9;
    $[70] = t14;
  } else {
    t14 = $[70];
  }
  return t14;
});
const FormInput = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const $ = _c(3);
  let t0;
  if ($[0] !== props || $[1] !== ref) {
    t0 = <InputField {...props} ref={ref} />;
    $[0] = props;
    $[1] = ref;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
});
const FormInputGroup = forwardRef<HTMLInputElement, FormInputGroupProps>((props, ref) => {
  const $ = _c(3);
  let t0;
  if ($[0] !== props || $[1] !== ref) {
    t0 = <InputField {...props} ref={ref} grouped={true} />;
    $[0] = props;
    $[1] = ref;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
});
FormInput.displayName = 'FormInput';
FormInputGroup.displayName = 'FormInputGroup';
export { FormInput, FormInputGroup };
export type { FormInputGroupProps, InputProps };